import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import SanityBlockContent from '@sanity/block-content-to-react';
import StaffList from '../components/StaffList';
import SEO from '../components/SEO';
import BackButton from '../components/BackButton';
import Gallery from '../components/Gallery';
import Video from '../components/Video';

const DivisionPageStyles = styled.div`
  margin: 40px 0;
`;

const DivisionImage = styled.div`
  width: 100%;
`;

const DivisionHeads = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 40px;
  grid-auto-rows: auto;
`;

const DivisionTopSection = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 40px;
`;

const ContentBlock = styled.div`
  margin-bottom: 40px;
`;
const VideoContainer = styled.div`
  margin-top: 40px;
`;

export default function DivisionPage({
  data: { divisionPage, staffs, staffImage, pageStrings },
}) {
  const defaultImage = staffImage.nodes;
  const divisionalStaff = staffs.nodes
    .filter((singleStaff) => singleStaff.division?.name === divisionPage.name) // Sort by Inspector first followed by callsign for sergeants
    .sort((a, b) =>
      // eslint-disable-next-line no-nested-ternary
      a.rank.name === 'Inspector'
        ? -1
        : b.rank.name === 'Inspector'
        ? 1
        : a.callsign.localeCompare(b.callsign)
    );
    const schema = {
      "@context": "https://schema.org/", 
      "@type": "WebPage", 
      "name": divisionPage.name,
      "description": divisionPage.seo.seoDescription,
      "inLanguage": "en-GB",
      "datePublished": divisionPage._createdAt,
      "dateModified": divisionPage._updatedAt,
      "image": [
          {
              "@id": divisionPage.divisionImage.asset.url
          },
      ],
      "video": [
          {
              "@id": "https://www.youtube.com/watch?v=" + divisionPage.youtubeUrl
          },
      ],
  }
  return (
    <>
      <SEO title={divisionPage.name} pageSeo={divisionPage.seo} schemaData={schema} />
      <DivisionPageStyles>
        <BackButton />
        <h1>{divisionPage.name}</h1>
        <DivisionTopSection>
          <DivisionImage>
            {divisionPage._rawDivisionCarousel !== null ? (
              <Gallery images={divisionPage._rawDivisionCarousel} />
            ) : (
              <GatsbyImage
                image={divisionPage.divisionImage.asset.gatsbyImageData}
                alt={divisionPage.name}
              />
            )}
          </DivisionImage>
        </DivisionTopSection>
        <ContentBlock>
          <SanityBlockContent blocks={divisionPage._rawContent} />
        </ContentBlock>
        {divisionalStaff.length !== 0 ? (
          <>
            <h2>{pageStrings.nodes[0].divisionPageMeetOurTeamTitle}</h2>

            <DivisionHeads>
              {divisionalStaff.map((staff) => (
                <StaffList
                  key={staff.id}
                  staff={staff}
                  defaultImage={defaultImage}
                  includeDivision={false}
                />
              ))}
            </DivisionHeads>
          </>
        ) : (
          ''
        )}

        {divisionPage.youtubeUrl ? (
          <VideoContainer>
            <Video videoId={divisionPage.youtubeUrl} name={divisionPage.name} />
          </VideoContainer>
        ) : (
          ''
        )}
      </DivisionPageStyles>
    </>
  );
}

export const query = graphql`
  query ($slug: String!) {
    divisionPage: sanityDivision(slug: { current: { eq: $slug } }) {
      name
      _createdAt
      _updatedAt
      abbreviation
      slug {
        current
      }
      teaserContent
      youtubeUrl
      _rawDivisionCarousel
      _rawContent(resolveReferences: { maxDepth: 10 })
      divisionImage {
        asset {
          gatsbyImageData(width: 1280, placeholder: BLURRED, breakpoints: [425, 768, 960])
          url
        }
      }
      seo {
        seoTitle
        seoDescription
        canonicalLink
        metaTags
        seoMetaImage {
          asset {
            url
          }
        }
      }
    }
    staffs: allSanityStaff {
      nodes {
        id
        name
        callsign
        image {
          asset {
            gatsbyImageData(width: 250, height: 250, placeholder: BLURRED, breakpoints: [425, 768, 960])
          }
        }
        rank {
          id
          name
        }
        division {
          name
        }
      }
    }
    staffImage: allSanitySiteSettings {
      nodes {
        staffImage {
          asset {
            gatsbyImageData(width: 250, height: 175, placeholder: BLURRED, breakpoints: [425, 768, 960])
          }
        }
      }
    }
    pageStrings: allSanityPageString {
      nodes {
        divisionPageMeetOurTeamTitle
      }
    }
  }
`;
